import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Stack, Tooltip, Typography, alpha } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BarChartIcon from '@mui/icons-material/BarChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { green, red } from '@mui/material/colors';
import { SCUIGeneralDrawer } from '../../../atomic/molecules';
import { SCUIDetailStockSearchDrawerContent } from './SCUIDetailStockSearchDrawerContent';
import { DST } from '../detailStockSearch.classes';

interface IProps {
    datas: any;
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; bgColor: string; borderColor: string }> = {
    Inventory: {
        icon: <InventoryIcon sx={{ fontSize: 15 }} />,
        color: '#1565c0',
        bgColor: '#e3f2fd',
        borderColor: '#90caf9',
    },
    Warehouse: {
        icon: <WarehouseIcon sx={{ fontSize: 15 }} />,
        color: '#2e7d32',
        bgColor: '#e8f5e9',
        borderColor: '#a5d6a7',
    },
    ProductMapper: {
        icon: <AccountTreeIcon sx={{ fontSize: 15 }} />,
        color: '#6a1b9a',
        bgColor: '#f3e5f5',
        borderColor: '#ce93d8',
    },
    StockView: {
        icon: <BarChartIcon sx={{ fontSize: 15 }} />,
        color: '#e65100',
        bgColor: '#fff3e0',
        borderColor: '#ffcc80',
    },
};

const StatusChip: FC<{ status: string }> = ({ status }) => {
    const isTrue = status === 'true';
    return (
        <Chip
            size="small"
            icon={
                isTrue ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: '14px !important' }} />
                ) : (
                    <CancelRoundedIcon sx={{ fontSize: '14px !important' }} />
                )
            }
            label={status}
            color={isTrue ? 'success' : 'error'}
            sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 70 }}
        />
    );
};

const ExistsIcon: FC<{ exists: boolean; title: string; icon: React.ReactNode }> = ({ exists, title, icon }) => (
    <Tooltip title={title} arrow placement="top">
        <Box
            sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: exists ? alpha(green[500], 0.1) : alpha(red[500], 0.1),
                border: `1.5px solid ${exists ? green[300] : red[300]}`,
                cursor: 'default',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${exists ? alpha(green[500], 0.35) : alpha(red[500], 0.35)}`,
                },
                '& svg': {
                    fontSize: 17,
                    color: exists ? green[700] : red[700],
                },
            }}
        >
            {icon}
        </Box>
    </Tooltip>
);

const HEADER_CELL_SX = {
    fontWeight: 700,
    fontSize: '0.72rem',
    color: '#374151',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    py: 1.75,
    borderBottom: '2px solid #dde3f0',
    whiteSpace: 'nowrap' as const,
};

export const Demo: FC<IProps> = ({ datas }) => {
    const onDrawerOpen = () => setDrawerOpen(true);
    const onDrawerClose = () => setDrawerOpen(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [product, setProduct] = useState<any>();
    const handleProduct = (item: any) => {
        setProduct(item);
        onDrawerOpen();
    };

    const { t } = useTranslation();

    let rows: any = [];

    if (datas.data.stockSummaries) {
        datas.data.stockSummaries.forEach((r: any, i: number) => {
            rows.push(
                createData(
                    r.productId,
                    r.size,
                    r.warehouseName,
                    r.inventoryQuantity,
                    r.stockViewQuantity,
                    r.warehouseStatus + '',
                    datas.data.stockSummaries[i].productMapperStatus + '',
                    r.inventoryExists,
                    r.warehouseExists,
                    r.productMappperExists,
                    r.stockViewExists,
                    datas.data.stockDetails[i].inventoryResult.message,
                    datas.data.stockDetails[i].warehouseResult.message,
                    datas.data.stockDetails[i].productMapperResult.message,
                    datas.data.stockDetails[i].stockViewResult.message,
                    datas.data.stockDetails[i].inventoryResult.inventoryEntity,
                    datas.data.stockDetails[i].warehouseResult.warehouseEntity,
                    datas.data.stockDetails[i].productMapperResult.productMapperEntity,
                    datas.data.stockDetails[i].stockViewResult.stockViewEntity,
                    datas.data.stockDetails[i].inventoryResult.status,
                    datas.data.stockDetails[i].warehouseResult.status,
                    datas.data.stockDetails[i].productMapperResult.status,
                    datas.data.stockDetails[i].stockViewResult.status
                )
            );
        });
    }

    function createData(
        productId: any,
        size: any,
        warehouse: any,
        inventoryQuantity: any,
        stockViewQuantity: any,
        warehouseStatus: any,
        productStatus: any,
        InventoryExists: any,
        WarehouseExists: any,
        ProductMapperExists: any,
        StockViewExists: any,
        inventoryMessage: any,
        warehouseMessage: any,
        productMapperMessage: any,
        stockMessage: any,
        InventoryEntity: any,
        WarehouseEntity: any,
        ProductMapperEntity: any,
        StockViewEntity: any,
        InventoryMapperStatus: any,
        WarehouseMapperStatus: any,
        ProductMapperStatus: any,
        StockMapperStatus: any
    ) {
        return {
            productId,
            size,
            warehouse,
            inventoryQuantity,
            stockViewQuantity,
            warehouseStatus,
            productStatus,
            InventoryExists,
            WarehouseExists,
            ProductMapperExists,
            StockViewExists,
            inventoryMessage,
            warehouseMessage,
            productMapperMessage,
            stockMessage,
            InventoryEntity,
            WarehouseEntity,
            ProductMapperEntity,
            StockViewEntity,
            InventoryMapperStatus,
            WarehouseMapperStatus,
            ProductMapperStatus,
            StockMapperStatus,

            history: [
                {
                    type: 'Inventory',
                    status: InventoryMapperStatus + '',
                    message: inventoryMessage,
                    entity: InventoryEntity,
                },
                {
                    type: 'Warehouse',
                    status: WarehouseMapperStatus + '',
                    message: warehouseMessage,
                    entity: WarehouseEntity,
                },
                {
                    type: 'ProductMapper',
                    status: ProductMapperStatus + '',
                    message: productMapperMessage,
                    entity: ProductMapperEntity,
                },
                {
                    type: 'StockView',
                    status: StockMapperStatus + '',
                    message: stockMessage,
                    entity: StockViewEntity,
                },
            ],
        };
    }

    function Row(props: { row: ReturnType<typeof createData> }) {
        const { row } = props;
        const [open, setOpen] = React.useState(false);
        return (
            <React.Fragment>
                <SCUIGeneralDrawer
                    open={drawerOpen}
                    onClose={(_: any, reason: any) => {
                        if (reason === 'backdropClick') {
                            setDrawerOpen(false);
                        }
                    }}
                >
                    <SCUIDetailStockSearchDrawerContent
                        datas={product}
                        onClose={onDrawerClose}
                    ></SCUIDetailStockSearchDrawerContent>
                </SCUIGeneralDrawer>

                <TableRow
                    sx={{
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: alpha('#6366f1', 0.03) },
                        '& > *': { borderBottom: 'unset' },
                    }}
                >
                    <TableCell sx={{ width: 48, pr: 0 }}>
                        <IconButton
                            className={DST.inputIconExpandRow}
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                            sx={{
                                bgcolor: open ? alpha('#6366f1', 0.1) : 'transparent',
                                border: '1px solid',
                                borderColor: open ? alpha('#6366f1', 0.3) : 'divider',
                                color: open ? '#6366f1' : 'text.secondary',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: alpha('#6366f1', 0.1),
                                    borderColor: alpha('#6366f1', 0.3),
                                    color: '#6366f1',
                                },
                            }}
                        >
                            <KeyboardArrowDownIcon
                                sx={{
                                    transition: 'transform 0.25s ease',
                                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                    fontSize: 18,
                                }}
                            />
                        </IconButton>
                    </TableCell>

                    <TableCell
                        component="th"
                        scope="row"
                        sx={{ fontWeight: 600, fontSize: '0.82rem', color: 'text.primary', fontFamily: 'monospace' }}
                    >
                        {row.productId}
                    </TableCell>

                    <TableCell align="center">
                        <Chip
                            label={row.size}
                            size="small"
                            sx={{
                                bgcolor: '#f0f4ff',
                                color: '#3730a3',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                border: '1px solid #c7d2fe',
                                minWidth: 40,
                            }}
                        />
                    </TableCell>

                    <TableCell align="center">
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}
                        >
                            {row.warehouse}
                        </Typography>
                    </TableCell>

                    <TableCell align="center">
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 36,
                                height: 28,
                                borderRadius: 1.5,
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                px: 1,
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                                {row.inventoryQuantity}
                            </Typography>
                        </Box>
                    </TableCell>

                    <TableCell align="center">
                        <StatusChip status={row.warehouseStatus} />
                    </TableCell>

                    <TableCell align="center">
                        <StatusChip status={row.productStatus} />
                    </TableCell>

                    <TableCell align="center">
                        <Stack direction="row" spacing={0.75} justifyContent="center">
                            <ExistsIcon
                                exists={row.InventoryExists}
                                title={t('productSearch.inventoryExists')}
                                icon={<InventoryIcon />}
                            />
                            <ExistsIcon
                                exists={row.WarehouseExists}
                                title={t('productSearch.warehouseExists')}
                                icon={<WarehouseIcon />}
                            />
                            <ExistsIcon
                                exists={row.ProductMapperExists}
                                title={t('productSearch.productExists')}
                                icon={<AccountTreeIcon />}
                            />
                            <ExistsIcon
                                exists={row.StockViewExists}
                                title={t('productSearch.stockExists')}
                                icon={<BarChartIcon />}
                            />
                        </Stack>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={8}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box
                                sx={{
                                    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                                    borderTop: '1px solid #e2e8f0',
                                    borderBottom: '1px solid #e2e8f0',
                                    px: 4,
                                    py: 1.5,
                                }}
                            >
                                <Table size="small" aria-label="stock details">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{ ...HEADER_CELL_SX, width: 32, borderBottom: '1px solid #dde3f0' }}
                                            />
                                            <TableCell sx={{ ...HEADER_CELL_SX, borderBottom: '1px solid #dde3f0' }}>
                                                {t('productSearch.type')}
                                            </TableCell>
                                            <TableCell sx={{ ...HEADER_CELL_SX, borderBottom: '1px solid #dde3f0' }}>
                                                {t('productSearch.status')}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...HEADER_CELL_SX,
                                                    borderBottom: '1px solid #dde3f0',
                                                    width: '50%',
                                                }}
                                            >
                                                {t('productSearch.message')}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{ ...HEADER_CELL_SX, borderBottom: '1px solid #dde3f0' }}
                                            >
                                                {t('productSearch.detail.inStock')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.history.map((historyRow) => {
                                            const typeConfig = TYPE_CONFIG[historyRow.type];
                                            return (
                                                <TableRow
                                                    key={historyRow.message}
                                                    sx={{
                                                        '&:last-child td': { border: 0 },
                                                        transition: 'background-color 0.15s ease',
                                                        '&:hover': { bgcolor: alpha('#6366f1', 0.03) },
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 1, border: 0 }}>
                                                        {typeConfig && (
                                                            <Box
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: typeConfig.bgColor,
                                                                    border: `1px solid ${typeConfig.borderColor}`,
                                                                    '& svg': { fontSize: 15, color: typeConfig.color },
                                                                }}
                                                            >
                                                                {typeConfig.icon}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" sx={{ py: 1, border: 0 }}>
                                                        {typeConfig ? (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: '0.78rem',
                                                                    color: typeConfig.color,
                                                                }}
                                                            >
                                                                {historyRow.type}
                                                            </Typography>
                                                        ) : (
                                                            historyRow.type
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1, border: 0 }}>
                                                        <StatusChip status={historyRow.status} />
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            py: 1,
                                                            border: 0,
                                                            fontSize: '0.78rem',
                                                            color: 'text.secondary',
                                                            maxWidth: 400,
                                                        }}
                                                    >
                                                        {historyRow.message}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ py: 1, border: 0 }}>
                                                        <Tooltip title={t('productSearch.detail.inStock')} arrow>
                                                            <IconButton
                                                                className={DST.inputIconEntityDetail}
                                                                size="small"
                                                                onClick={() => handleProduct(historyRow.entity)}
                                                                sx={{
                                                                    color: '#6366f1',
                                                                    bgcolor: alpha('#6366f1', 0.07),
                                                                    border: '1px solid',
                                                                    borderColor: alpha('#6366f1', 0.25),
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        bgcolor: alpha('#6366f1', 0.15),
                                                                        transform: 'scale(1.1)',
                                                                        boxShadow: `0 3px 10px ${alpha('#6366f1', 0.3)}`,
                                                                    },
                                                                }}
                                                            >
                                                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }

    const CollapsibleTable = () => {
        return (
            <TableContainer
                className={DST.tableContainer}
                component={Paper}
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
            >
                <Table aria-label="collapsible table">
                    <TableHead>
                        <TableRow
                            sx={{
                                background: 'linear-gradient(135deg, #f0f4ff 0%, #e8edf8 100%)',
                            }}
                        >
                            <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
                            <TableCell sx={HEADER_CELL_SX}>{t('detailedStockSearch.table.productId')}</TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.size')}
                            </TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.warehouse')}
                            </TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.stockCount')}
                            </TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.warehouseStatus')}
                            </TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.productStatus')}
                            </TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>
                                {t('detailedStockSearch.table.exists')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row: any) => (
                            <Row key={row.optionId} row={row} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return CollapsibleTable();
};
